import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { UserButton, useUser, useAuth } from '@clerk/clerk-react'
import { Bell, Search, X, FileText, FileCheck, FileStack, Loader2 } from 'lucide-react'
import { SidebarToggle } from './Sidebar'

interface HeaderProps {
  onMenuClick: () => void
}

interface SearchResult {
  intakes: Array<{
    id: string
    orderNumber: string
    status: string
    tenantName: string
    propertyAddress: string
  }>
  contracts: Array<{
    id: string
    orderNumber: string
    status: string
    templateName: string
    propertyAddress: string
  }>
  templates: Array<{
    id: string
    name: string
    type: string
    isActive: boolean
  }>
}

export function Header({ onMenuClick }: HeaderProps) {
  const { user } = useUser()
  const { getToken } = useAuth()
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<SearchResult | null>(null)
  const [isSearching, setIsSearching] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)

  // Debounced search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults(null)
      setShowDropdown(false)
      return
    }

    const timer = setTimeout(async () => {
      setIsSearching(true)
      try {
        const token = await getToken()
        const apiUrl = import.meta.env.VITE_API_URL || ''
        const res = await fetch(
          `${apiUrl}/api/search?q=${encodeURIComponent(searchQuery)}&limit=10`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        )
        if (res.ok) {
          const data = await res.json()
          if (data.success) {
            setSearchResults(data.data)
            setShowDropdown(true)
          }
        }
      } catch (err) {
        console.error('Search error:', err)
      } finally {
        setIsSearching(false)
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [searchQuery, getToken])

  // Keyboard shortcut Cmd/Ctrl+K
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        inputRef.current?.focus()
      }
      if (e.key === 'Escape') {
        setShowDropdown(false)
        inputRef.current?.blur()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleResultClick = useCallback(
    (type: 'intake' | 'contract' | 'template', id: string) => {
      setShowDropdown(false)
      setSearchQuery('')
      if (type === 'intake') {
        navigate(`/intakes/${id}`)
      } else if (type === 'contract') {
        navigate(`/contracts/${id}`)
      } else if (type === 'template') {
        navigate(`/templates/${id}`)
      }
    },
    [navigate]
  )

  const clearSearch = () => {
    setSearchQuery('')
    setSearchResults(null)
    setShowDropdown(false)
    inputRef.current?.focus()
  }

  const hasResults =
    searchResults &&
    (searchResults.intakes.length > 0 ||
      searchResults.contracts.length > 0 ||
      searchResults.templates.length > 0)

  return (
    <header className="sticky top-0 z-30 h-16 bg-white border-b border-neutral-200">
      <div className="flex items-center justify-between h-full px-4 lg:px-6">
        {/* Left side */}
        <div className="flex items-center gap-4">
          <SidebarToggle onClick={onMenuClick} />

          {/* Search */}
          <div className="hidden sm:flex items-center" ref={dropdownRef}>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <input
                ref={inputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => searchResults && setShowDropdown(true)}
                placeholder="Buscar... (Cmd+K)"
                className="w-64 lg:w-80 h-9 pl-9 pr-8 rounded-lg border border-neutral-200 bg-neutral-50 text-sm placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
              />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-neutral-200 rounded"
                >
                  {isSearching ? (
                    <Loader2 className="w-3 h-3 text-neutral-400 animate-spin" />
                  ) : (
                    <X className="w-3 h-3 text-neutral-400" />
                  )}
                </button>
              )}

              {/* Search Dropdown */}
              {showDropdown && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg max-h-96 overflow-y-auto">
                  {!hasResults && !isSearching && (
                    <div className="p-4 text-center text-sm text-neutral-500">
                      No se encontraron resultados para "{searchQuery}"
                    </div>
                  )}

                  {searchResults?.intakes && searchResults.intakes.length > 0 && (
                    <div>
                      <div className="px-3 py-2 text-xs font-semibold text-neutral-500 bg-neutral-50 border-b border-neutral-100">
                        Ordenes
                      </div>
                      {searchResults.intakes.map((item) => (
                        <button
                          key={item.id}
                          onClick={() => handleResultClick('intake', item.id)}
                          className="w-full flex items-center gap-3 px-3 py-2 hover:bg-neutral-50 text-left"
                        >
                          <FileText className="w-4 h-4 text-neutral-400 flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-neutral-900 truncate">
                              {item.orderNumber}
                            </p>
                            <p className="text-xs text-neutral-500 truncate">
                              {item.tenantName} - {item.propertyAddress}
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {searchResults?.contracts && searchResults.contracts.length > 0 && (
                    <div>
                      <div className="px-3 py-2 text-xs font-semibold text-neutral-500 bg-neutral-50 border-b border-neutral-100">
                        Contratos
                      </div>
                      {searchResults.contracts.map((item) => (
                        <button
                          key={item.id}
                          onClick={() => handleResultClick('contract', item.id)}
                          className="w-full flex items-center gap-3 px-3 py-2 hover:bg-neutral-50 text-left"
                        >
                          <FileCheck className="w-4 h-4 text-neutral-400 flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-neutral-900 truncate">
                              {item.orderNumber}
                            </p>
                            <p className="text-xs text-neutral-500 truncate">
                              {item.templateName} - {item.propertyAddress}
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {searchResults?.templates && searchResults.templates.length > 0 && (
                    <div>
                      <div className="px-3 py-2 text-xs font-semibold text-neutral-500 bg-neutral-50 border-b border-neutral-100">
                        Plantillas
                      </div>
                      {searchResults.templates.map((item) => (
                        <button
                          key={item.id}
                          onClick={() => handleResultClick('template', item.id)}
                          className="w-full flex items-center gap-3 px-3 py-2 hover:bg-neutral-50 text-left"
                        >
                          <FileStack className="w-4 h-4 text-neutral-400 flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-neutral-900 truncate">
                              {item.name}
                            </p>
                            <p className="text-xs text-neutral-500 truncate">
                              {item.type} {!item.isActive && '(Inactiva)'}
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right side */}
        <div className="flex items-center gap-3">
          {/* Mobile search button */}
          <button
            className="sm:hidden p-2 rounded-lg hover:bg-neutral-100 transition-colors"
            aria-label="Buscar"
            onClick={() => inputRef.current?.focus()}
          >
            <Search className="w-5 h-5 text-neutral-600" />
          </button>

          {/* Notifications */}
          <button
            className="relative p-2 rounded-lg hover:bg-neutral-100 transition-colors"
            aria-label="Notificaciones"
          >
            <Bell className="w-5 h-5 text-neutral-600" />
            <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-danger-500 rounded-full" />
          </button>

          {/* User info and menu */}
          <div className="flex items-center gap-3 pl-3 border-l border-neutral-200">
            <div className="hidden md:block text-right">
              <p className="text-sm font-medium text-neutral-900">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="text-xs text-neutral-500">{user?.primaryEmailAddress?.emailAddress}</p>
            </div>
            <UserButton
              appearance={{
                elements: {
                  avatarBox: 'w-9 h-9',
                },
              }}
            />
          </div>
        </div>
      </div>
    </header>
  )
}
